import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = "/api";

// Extract plain text from Quill Delta JSON for preview
const extractIntro = (intro, contentJson) => {
  // Prefer the plain intro field; fall back to contentJson if absent
  if (intro) return intro;
  try {
    const parsed = JSON.parse(contentJson);
    return parsed.ops
      .map((op) => op.insert)
      .join("")
      .replace(/\n+/g, " ")
      .trim();
  } catch {
    return "";
  }
};

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric",
  });
};

const formatTime = (t) => {
  if (!t) return "";
  const [hours, minutes] = t.split(":");
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? "PM" : "AM";
  return `${hour % 12 || 12}:${minutes} ${ampm}`;
};

const Ministry = () => {
  const [articles, setArticles]         = useState([]);
  const [categories, setCategories]     = useState(["All"]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [searchTerm, setSearchTerm]     = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  // ── Fetch articles ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/ministry-articles/`, {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        });
        if (!response.ok) throw new Error(`Failed to fetch articles: ${response.status}`);
        const data = await response.json();
        const results = Array.isArray(data) ? data : (data.results ?? []);
        setArticles(results);

        // Derive categories from category_display values
        const uniqueCategories = [
          "All",
          ...Array.from(new Set(results.map((a) => a.category_display).filter(Boolean))),
        ];
        setCategories(uniqueCategories);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  // ── Filter ──────────────────────────────────────────────────────────────
  const filteredArticles = articles.filter((article) => {
    const matchesCategory =
      activeCategory === "All" || article.category_display === activeCategory;
    const term = searchTerm.toLowerCase();
    const preview = extractIntro(article.intro, article.contentJson);
    const matchesSearch =
      (article.title || "").toLowerCase().includes(term) ||
      preview.toLowerCase().includes(term);
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css" />

      <div className="ministry-page">

        {/* HERO */}
        <section className="ministry-hero">
          <div className="hero-overlay">
            <div className="container">
              <h1 className="display-3 fw-bold text-white mb-4">Ministry Works</h1>
              <p className="fs-5 text-white mb-5">Discover how we serve God and our community</p>
              <div className="search-container mx-auto">
                <div className="input-group input-group-lg">
                  <span className="input-group-text bg-white border-0">
                    <i className="bi bi-search text-primary"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-0 shadow-sm"
                    placeholder="Search ministry articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORIES */}
        <section className="py-4 bg-light sticky-top shadow-sm" style={{ zIndex: 1020 }}>
          <div className="container">
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`btn ${activeCategory === category ? "btn-primary" : "btn-outline-primary"} rounded-pill px-4`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ARTICLES LIST */}
        <section className="py-5 bg-white">
          <div className="container">

            {/* Loading */}
            {loading && (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div style={{ width: 44, height: 44, border: "4px solid #e8f4fd", borderTopColor: "#0b1c6b", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="text-center py-5">
                <div style={{ fontSize: "3.5rem", marginBottom: 16 }}>⚠️</div>
                <h4 style={{ color: "#0b1c6b" }}>Could not load articles</h4>
                <p className="text-muted">{error}</p>
              </div>
            )}

            {/* Content */}
            {!loading && !error && (
              <>
                <div className="row mb-4">
                  <div className="col">
                    <p className="text-muted mb-0">
                      <i className="bi bi-journal-text me-2"></i>
                      Showing {filteredArticles.length} article{filteredArticles.length !== 1 ? "s" : ""}
                      {activeCategory !== "All" && ` in ${activeCategory}`}
                    </p>
                  </div>
                </div>

                {filteredArticles.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                    <h5 className="text-muted">No articles found</h5>
                    <p className="text-muted">Try adjusting your search or category filter</p>
                  </div>
                ) : (
                  <div className="row g-4">
                    {filteredArticles.map((article) => {
                      const preview = extractIntro(article.intro, article.contentJson);
                      return (
                        <div className="col-12" key={article.id}>
                          <article className="card border-0 shadow-sm rounded-4 overflow-hidden article-card">
                            <div className="row g-0">
                              <div className="col-md-4">
                                {article.cover_image ? (
                                  <img
                                    src={article.cover_image}
                                    alt={article.title}
                                    className="img-fluid h-100 w-100"
                                    style={{ objectFit: "cover", minHeight: "280px" }}
                                  />
                                ) : (
                                  <div style={{ minHeight: 280, background: "linear-gradient(135deg,#0b1c6b,#159EEC)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "4rem" }}>
                                    ✝️
                                  </div>
                                )}
                              </div>
                              <div className="col-md-8">
                                <div className="card-body p-4 p-md-5">
                                  <div className="mb-3">
                                    {article.category_display && (
                                      <span className="badge bg-info bg-opacity-10 text-primary text-uppercase me-2">
                                        {article.category_display}
                                      </span>
                                    )}
                                  </div>

                                  <h2 className="card-title display-6 fw-bold text-primary mb-3">
                                    {article.title}
                                  </h2>

                                  <div className="d-flex align-items-center gap-4 mb-3 text-muted">
                                    {article.article_date && (
                                      <span>
                                        <i className="bi bi-calendar3 me-2"></i>
                                        {formatDate(article.article_date)}
                                      </span>
                                    )}
                                    {article.article_time && (
                                      <span>
                                        <i className="bi bi-clock me-2"></i>
                                        {formatTime(article.article_time)}
                                      </span>
                                    )}
                                  </div>

                                  <p className="card-text fs-6 lh-lg mb-4">
                                    {preview.slice(0, 200)}{preview.length > 200 ? "..." : ""}
                                  </p>

                                  <Link
                                    to={`/ministry/${article.slug}`}
                                    className="btn btn-primary rounded-pill px-4"
                                  >
                                    Read More <i className="bi bi-arrow-right ms-2"></i>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </article>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* SOCIAL */}
        <section className="social-section py-5 text-center text-white">
          <div className="container py-4">
            <h2 className="display-5 fw-bold mb-3">Follow Us</h2>
            <p className="fs-6 mb-4 mx-auto" style={{ maxWidth: "600px" }}>
              Stay connected with Gospel City Church International through our social platforms.
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <a href="https://www.linkedin.com/in/gospel-city-church-international-650a562a0/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-linkedin fs-4"></i>
              </a>
              <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
            <i className="bi bi-chat-dots fs-4"></i>
            </a>
              <a href="https://x.com/GospelCityChur?t=x8dsFbFkghmYh02lV0FVxQ&s=03" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-twitter-x fs-4"></i>
              </a>
              <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-whatsapp fs-4"></i>
              </a>
              <a href="https://youtube.com/@gospelcitychurchinternational?si=1pZx2ak4ss3b4HuU" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-youtube fs-4"></i>
              </a>
            </div>
          </div>
        </section>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');
          * { font-family: 'Lexend', sans-serif; }

          .ministry-hero {
            position: relative;
            background-image: url('/images/church3.png');
            background-size: cover;
            background-position: center;
            height: 60vh;
            min-height: 500px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .hero-overlay { position: relative; width: 100%; text-align: center; z-index: 2; padding: 40px 20px; }
          .ministry-hero::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(to bottom, rgba(11,28,107,0.7), rgba(0,0,0,0.5));
            z-index: 1;
          }
          .search-container { max-width: 600px; }
          .input-group-text { padding: 12px 16px; }
          .form-control { padding: 12px 16px; }
          .form-control:focus { box-shadow: 0 0 0 0.25rem rgba(26,58,161,0.15); }
          .article-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
          .article-card:hover { transform: translateY(-5px); box-shadow: 0 15px 40px rgba(26,58,161,0.15) !important; }
          .text-primary { color: #1a3aa1 !important; }
          .text-info { color: #159EEC !important; }
          .bg-info { background-color: #159EEC !important; }
          .btn-primary { background: #0b1c6b; border-color: #0b1c6b; }
          .btn-primary:hover { background: #159EEC; border-color: #159EEC; }
          .btn-outline-primary { color: #0b1c6b; border-color: #0b1c6b; }
          .btn-outline-primary:hover { background: #0b1c6b; border-color: #0b1c6b; color: #fff; }
          .social-section { background: linear-gradient(135deg,#0b1c6b,#159EEC); }
          .social-icon { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; transition: all 0.3s; }
          .social-icon:hover { background: rgba(255,255,255,0.3); transform: translateY(-4px) scale(1.1); }
          @media (max-width: 768px) {
            .ministry-hero { height: 50vh; min-height: 400px; }
            .display-3 { font-size: 2.5rem; }
            .article-card .row { flex-direction: column; }
            .article-card .col-md-4 img { min-height: 200px; }
          }
        `}</style>
      </div>
    </>
  );
};

export default Ministry;